import React, { useState } from 'react';
import axios from 'axios';
import { Search, Coins, Plus, Minus } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WalletsTab = () => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [selected, setSelected] = useState(null);
  const [delta, setDelta] = useState(500);
  const [reason, setReason] = useState('Recarga MB WAY');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const search = async () => {
    setLoading(true); setErr(''); setRows([]);
    try {
      const { data } = await axios.get(`${API}/fantasy/admin/users/search`, { params: { q } });
      setRows(data);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };

  const adjust = async (sign) => {
    if (!selected) return;
    if (!Number(delta) || !reason.trim()) { setErr('Valor e motivo obrigatórios'); return; }
    setSaving(true); setErr(''); setMsg('');
    try {
      const signed = sign * Math.abs(Number(delta));
      const { data } = await axios.post(`${API}/fantasy/admin/wallet/adjust`, {
        user_id: selected.id, delta: signed, reason: reason.trim(),
      });
      setMsg(`✅ Novo saldo de ${selected.name}: ${data.new_balance.toLocaleString('pt-PT')} PC`);
      setSelected({ ...selected, procoins_balance: data.new_balance });
      setRows((prev) => prev.map((r) => r.id === selected.id ? { ...r, procoins_balance: data.new_balance } : r));
    } catch (ex) { setErr(formatApiError(ex)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="Pesquisar por nome ou email..."
              className="w-full bg-slate-800/70 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="wallets-search-input" />
          </div>
          <button onClick={search} disabled={loading}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 font-black rounded-lg px-4 py-2 text-sm"
            data-testid="wallets-search-btn">
            {loading ? 'A procurar...' : 'Procurar'}
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
      {msg && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid="wallets-msg">{msg}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden">
          <div className="px-4 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-slate-400">Resultados</div>
          {rows.length === 0
            ? <div className="p-6 text-slate-400 text-center text-sm">{loading ? 'A pesquisar...' : 'Sem resultados. Escreve pelo menos 1 caracter e clica Procurar.'}</div>
            : (
              <ul className="divide-y divide-white/5">
                {rows.map((r) => (
                  <li key={r.id}>
                    <button onClick={() => { setSelected(r); setMsg(''); setErr(''); }}
                      className={`w-full text-left px-4 py-3 hover:bg-white/5 ${selected?.id === r.id ? 'bg-emerald-500/10' : ''}`}
                      data-testid={`wallets-row-${r.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-white font-bold truncate">{r.name}</div>
                          <div className="text-slate-400 text-xs truncate">{r.email}</div>
                        </div>
                        <div className="text-amber-300 font-black text-sm tabular-nums whitespace-nowrap">
                          <Coins className="inline w-3.5 h-3.5 mr-0.5" />{r.procoins_balance.toLocaleString('pt-PT')} PC
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
        </div>

        <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Ajuste de saldo</div>
          {!selected
            ? <div className="text-slate-400 text-sm">Selecciona um utilizador na lista.</div>
            : (
              <div className="space-y-3">
                <div>
                  <div className="text-white font-black">{selected.name}</div>
                  <div className="text-slate-400 text-xs">{selected.email}</div>
                  <div className="mt-1 text-amber-300 font-black tabular-nums">
                    <Coins className="inline w-4 h-4 mr-1" />{selected.procoins_balance.toLocaleString('pt-PT')} PC
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-medium">Quantidade (PC)</label>
                  <input type="number" min="1" step="10" value={delta} onChange={(e) => setDelta(e.target.value)}
                    className="w-full mt-1 bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="wallets-delta-input" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-medium">Motivo</label>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120}
                    className="w-full mt-1 bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="wallets-reason-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => adjust(+1)} disabled={saving}
                    className="inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 font-black rounded-lg py-2 text-sm"
                    data-testid="wallets-add-btn">
                    <Plus className="w-4 h-4" /> Adicionar
                  </button>
                  <button onClick={() => adjust(-1)} disabled={saving}
                    className="inline-flex items-center justify-center gap-1.5 bg-red-500 hover:bg-red-400 disabled:opacity-50 text-slate-950 font-black rounded-lg py-2 text-sm"
                    data-testid="wallets-sub-btn">
                    <Minus className="w-4 h-4" /> Remover
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default WalletsTab;
